import React from 'react';
import type { ProductDetail, ProductVariant } from '@/api/models';
import { useUpdateVariantBarcodesMutation } from '@/api/queries';
import { showLocalizedToast } from '@/components/localizedToast';
import {
    addAlternateBarcode,
    barcodesRequestOf,
    checkBarcodesDraft,
    firstInvalidBarcodeField,
    isBarcodesDraftDirty,
    makeAlternateMain,
    placeBarcodesRefusal,
    removeAlternateBarcode,
    seedBarcodesDraft,
    type BarcodeFieldErrors,
    type BarcodeFieldId,
    type VariantBarcodesDraft,
} from './variantBarcodesFormMeta';

/** A field to focus once it renders; `seq` repeats a request for the same one. */
type BarcodeFocusRequest = { field: BarcodeFieldId; seq: number };

export type VariantBarcodesEditor = {
    /** The variant being edited, kept while the drawer closes. */
    variant: ProductVariant | null;
    isOpen: boolean;
    draft: VariantBarcodesDraft;
    errors: BarcodeFieldErrors;
    isDirty: boolean;
    isSaving: boolean;
    /**
     * The ref for a field's input, stable per field. The hook focuses the
     * first invalid field through it after the render that shows the error,
     * so the drawer runs no effect of its own.
     */
    inputRefOf: (field: BarcodeFieldId) => React.RefCallback<HTMLInputElement>;
    /**
     * Cancel, Escape, the backdrop or the close button. With unsaved edits it
     * asks first (`isDiscarding`); while a save runs it does nothing.
     */
    isDiscarding: boolean;
    onEdit: (variantId: string) => void;
    onClose: () => void;
    /** The question's Keep editing, or dismissing it: the draft stays. */
    onKeepEditing: () => void;
    /** The question's Discard changes: closes and drops the draft. */
    onDiscard: () => void;
    onMainChange: (value: string) => void;
    onAlternateChange: (key: string, value: string) => void;
    onAddTextChange: (value: string) => void;
    onFieldBlur: (field: BarcodeFieldId) => void;
    onAdd: () => void;
    onRemove: (key: string) => void;
    onMakeMain: (key: string) => void;
    onSave: () => void;
};

type UseVariantBarcodesFormParams = {
    productId: string | null;
    product: ProductDetail | null;
};

const EMPTY_DRAFT: VariantBarcodesDraft = {
    main: '',
    alternates: [],
    addText: '',
    nextKey: 0,
};

/**
 * The code and message of a rejected save: the mutation rejects with the
 * `ErrorResponse` of a refusal; anything else (a thrown bug) has neither.
 */
const refusalOf = (error: unknown): { code?: string; message?: string } => {
    if (typeof error !== 'object' || error === null) {
        return {};
    }
    const { code, message } = error as { code?: unknown; message?: unknown };
    return {
        ...(typeof code === 'string' ? { code } : {}),
        ...(typeof message === 'string' ? { message } : {}),
    };
};

const withoutError = (
    errors: BarcodeFieldErrors,
    field: BarcodeFieldId,
): BarcodeFieldErrors => {
    if (errors[field] === undefined) {
        return errors;
    }
    const next = { ...errors };
    delete next[field];
    return next;
};

/**
 * A variant's barcodes on the product page (bos-app#224, US-14, AC-12): the
 * barcodes drawer's draft, its field errors and its save.
 *
 * - **Edit** seeds the draft from the variant as saved and opens the drawer.
 * - **Add**, **Remove** and **Make main** change the draft only; **Save**
 *   sends the whole set in one `PUT …/barcodes`, a code still in the add
 *   field included. The mutation refreshes the detail and the lists.
 * - **Errors** follow rulebook §13: Save checks every field first and
 *   focuses the first invalid one, a blur checks its field, and editing a
 *   field clears its error. The hook focuses that field through its input's
 *   ref (`inputRefOf`) once it renders. A refusal with a barcode code goes on the field
 *   holding the code the message names (`placeBarcodesRefusal`) and keeps
 *   the drawer open; any other failure is a danger toast. Success toasts and
 *   closes.
 * - **Leaving** (rulebook §5.5): closing a dirty drawer opens the discard
 *   question instead. Keep editing leaves the drawer and its draft as they
 *   were; Discard closes, and the next Edit seeds the draft afresh.
 * - **Saving lock:** a second Save while one runs is ignored, and the drawer
 *   does not close meanwhile. An answer for a variant the drawer no longer
 *   edits is dropped.
 */
export const useVariantBarcodesForm = ({
    productId,
    product,
}: UseVariantBarcodesFormParams): VariantBarcodesEditor => {
    const mutation = useUpdateVariantBarcodesMutation();

    const [variantId, setVariantId] = React.useState<string | null>(null);
    const [isOpen, setIsOpen] = React.useState(false);
    const [draft, setDraft] = React.useState<VariantBarcodesDraft>(EMPTY_DRAFT);
    const [errors, setErrors] = React.useState<BarcodeFieldErrors>({});
    const [isSaving, setIsSaving] = React.useState(false);
    const [isDiscarding, setIsDiscarding] = React.useState(false);
    const [focusRequest, setFocusRequest] =
        React.useState<BarcodeFocusRequest | null>(null);

    // Each field's input as the drawer rendered it, and the stable ref
    // callback that keeps it there (rulebook §5.2: the effect lives here).
    const inputs = React.useRef(new Map<BarcodeFieldId, HTMLInputElement>());
    const inputRefs = React.useRef(
        new Map<BarcodeFieldId, React.RefCallback<HTMLInputElement>>(),
    );

    const savingLock = React.useRef(false);
    // The variant the drawer edits now, read after the request answers.
    const editing = React.useRef<string | null>(null);

    const variant =
        product?.variants.find((candidate) => candidate.id === variantId) ??
        null;

    const isDirty = variant !== null && isBarcodesDraftDirty(draft, variant);

    const inputRefOf = React.useCallback((field: BarcodeFieldId) => {
        const known = inputRefs.current.get(field);
        if (known) {
            return known;
        }
        const ref: React.RefCallback<HTMLInputElement> = (element) => {
            if (element) {
                inputs.current.set(field, element);
            } else {
                inputs.current.delete(field);
            }
        };
        inputRefs.current.set(field, ref);
        return ref;
    }, []);

    // After the render that shows the error, so the input the request names
    // is the one on screen.
    React.useEffect(() => {
        if (focusRequest !== null) {
            inputs.current.get(focusRequest.field)?.focus();
        }
    }, [focusRequest]);

    const focus = React.useCallback((field: BarcodeFieldId) => {
        setFocusRequest((previous) => ({
            field,
            seq: (previous?.seq ?? 0) + 1,
        }));
    }, []);

    const onEdit = React.useCallback(
        (id: string) => {
            const picked = product?.variants.find(
                (candidate) => candidate.id === id,
            );
            if (!picked) {
                return;
            }
            editing.current = id;
            setVariantId(id);
            setDraft(seedBarcodesDraft(picked));
            setErrors({});
            setIsDiscarding(false);
            setIsOpen(true);
        },
        [product],
    );

    const closeDrawer = React.useCallback(() => {
        editing.current = null;
        setIsDiscarding(false);
        setIsOpen(false);
    }, []);

    const onClose = React.useCallback(() => {
        if (savingLock.current) {
            return;
        }
        if (isDirty) {
            setIsDiscarding(true);
            return;
        }
        closeDrawer();
    }, [closeDrawer, isDirty]);

    const onKeepEditing = React.useCallback(() => setIsDiscarding(false), []);

    const onMainChange = React.useCallback((value: string) => {
        setDraft((current) => ({ ...current, main: value }));
        setErrors((current) => withoutError(current, 'main'));
    }, []);

    const onAlternateChange = React.useCallback(
        (key: string, value: string) => {
            setDraft((current) => ({
                ...current,
                alternates: current.alternates.map((alternate) =>
                    alternate.key === key ? { ...alternate, value } : alternate,
                ),
            }));
            setErrors((current) => withoutError(current, key));
        },
        [],
    );

    const onAddTextChange = React.useCallback((value: string) => {
        setDraft((current) => ({ ...current, addText: value }));
        setErrors((current) => withoutError(current, 'add'));
    }, []);

    const onFieldBlur = React.useCallback(
        (field: BarcodeFieldId) => {
            if (field === 'add') {
                return;
            }
            const found = checkBarcodesDraft(draft)[field];
            if (found) {
                setErrors((current) => ({ ...current, [field]: found }));
            }
        },
        [draft],
    );

    const onAdd = React.useCallback(() => {
        const added = addAlternateBarcode(draft);
        if (added.error) {
            setErrors((current) => ({ ...current, add: added.error! }));
            focus('add');
            return;
        }
        setDraft(added.draft);
    }, [draft, focus]);

    const onRemove = React.useCallback((key: string) => {
        setDraft((current) => removeAlternateBarcode(current, key));
        setErrors((current) => withoutError(current, key));
    }, []);

    const onMakeMain = React.useCallback((key: string) => {
        setDraft((current) => makeAlternateMain(current, key));
        // The two codes swapped fields, so their errors no longer fit.
        setErrors((current) =>
            withoutError(withoutError(current, key), 'main'),
        );
    }, []);

    const save = React.useCallback(async () => {
        if (savingLock.current || !productId || !variant) {
            return;
        }
        savingLock.current = true;
        try {
            const added = addAlternateBarcode(draft);
            const ready = added.draft;
            const found: BarcodeFieldErrors = {
                ...checkBarcodesDraft(ready),
                ...(added.error ? { add: added.error } : {}),
            };
            const first = firstInvalidBarcodeField(ready, found);
            setDraft(ready);
            if (first !== null) {
                setErrors(found);
                focus(first);
                return;
            }

            setIsSaving(true);
            const savedFor = variant.id;
            try {
                await mutation.mutateAsync({
                    productId,
                    variantId: savedFor,
                    body: barcodesRequestOf(ready),
                });
            } catch (error) {
                if (editing.current !== savedFor) {
                    return;
                }
                const placed = placeBarcodesRefusal(ready, refusalOf(error));
                if (placed) {
                    setErrors(placed);
                    const field = firstInvalidBarcodeField(ready, placed);
                    if (field !== null) {
                        focus(field);
                    }
                    return;
                }
                showLocalizedToast('danger', {
                    key: 'products.detail.barcodes.failed',
                });
                return;
            }
            if (editing.current !== savedFor) {
                return;
            }
            showLocalizedToast('success', {
                key: 'products.detail.barcodes.saved',
            });
            closeDrawer();
        } finally {
            savingLock.current = false;
            setIsSaving(false);
        }
    }, [closeDrawer, draft, focus, mutation, productId, variant]);

    const onSave = React.useCallback(() => {
        void save();
    }, [save]);

    return {
        variant,
        isOpen: isOpen && variant !== null,
        draft,
        errors,
        isDirty,
        isSaving,
        inputRefOf,
        isDiscarding,
        onEdit,
        onClose,
        onKeepEditing,
        onDiscard: closeDrawer,
        onMainChange,
        onAlternateChange,
        onAddTextChange,
        onFieldBlur,
        onAdd,
        onRemove,
        onMakeMain,
        onSave,
    };
};
